'use client'

import type { FeatureListItem, ModuleKey } from '@cloud/modules'
import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { type ModulesListItem, useModules } from '@/hooks/use-modules'

/**
 * Context value type for the ModulesContext
 */
interface ModulesContextValue {
  /** All modules with their status */
  modules: ModulesListItem[]
  /** Whether the modules are currently loading */
  isLoading: boolean
  /** Whether there was an error loading modules */
  isError: boolean
  /** The error object if loading failed */
  error: Error | null
  /**
   * Check if a module is enabled for the current tenant
   * @param key - The module key to check (hr, finance, inventory, crm, projects)
   * @returns true if the module status is 'active'
   */
  isModuleEnabled: (key: ModuleKey | string) => boolean
  /**
   * Check if a feature is enabled for a specific module
   * @param moduleKey - The module key
   * @param featureKey - The feature key to check
   * @returns true if the feature is enabled
   */
  isFeatureEnabled: (moduleKey: ModuleKey | string, featureKey: string) => boolean
  /**
   * Get a specific module by key
   * @param key - The module key
   * @returns The module data or undefined if not found
   */
  getModule: (key: ModuleKey | string) => ModulesListItem | undefined
  /**
   * Get features for a specific module
   * @param moduleKey - The module key
   * @returns Array of features with their enabled status
   */
  getModuleFeatures: (moduleKey: ModuleKey | string) => FeatureListItem[]
  /**
   * Refetch modules data
   */
  refetch: () => void
}

const ModulesContext = createContext<ModulesContextValue | undefined>(undefined)

/**
 * Hook to access the modules context
 *
 * @throws Error if used outside of ModulesProvider
 */
export function useModulesContext(): ModulesContextValue {
  const context = useContext(ModulesContext)
  if (!context) {
    throw new Error('useModulesContext must be used within a ModulesProvider')
  }
  return context
}

/**
 * Modules Provider
 *
 * Provides module state to the entire application.
 * Wraps the useModules hook to share module data across components.
 *
 * Should be placed in the provider tree after QueryProvider and UserProvider
 * to ensure auth is available for the API calls.
 */
export function ModulesProvider({ children }: { children: ReactNode }) {
  const {
    modules,
    isLoading,
    isError,
    error,
    isModuleEnabled,
    isFeatureEnabled,
    getModule,
    getModuleFeatures,
    refetch,
  } = useModules()

  const value = useMemo<ModulesContextValue>(
    () => ({
      modules,
      isLoading,
      isError,
      error,
      isModuleEnabled,
      isFeatureEnabled,
      getModule,
      getModuleFeatures,
      refetch,
    }),
    [
      modules,
      isLoading,
      isError,
      error,
      isModuleEnabled,
      isFeatureEnabled,
      getModule,
      getModuleFeatures,
      refetch,
    ],
  )

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>
}
